import { Fragment, useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Icon } from '@iconify/react'
import type {
  RaspadinhaContemplatedPrizeImage,
  RaspadinhaDayWinningSchedule,
  RaspadinhaTimeSlotWinner,
  RaspadinhaWinningTimeSlot,
} from '@/types/raspadinha-campaign'
import {
  addGeneratedWinningTimeSlots,
  addWinnerToTimeSlot,
  addWinningTimeSlot,
  ADD_TIME_SLOT_REQUIRES_PRIZE_CATALOG_MESSAGE,
  buildContemplatedPrizeSelectOptions,
  canClearWinningTimeSlotPrize,
  canRemoveWinningTimeSlot,
  canSetWinningTimeSlotTime,
  createMockPrizeReceiptSignature,
  ensureDateWinningSchedules,
  formatCampaignDateLabel,
  getCampaignDateStatus,
  getContemplatedPrizeTitle,
  hasWinningTimeSlotPrize,
  isValidWinningTime,
  countWinnersInDaySchedule,
  REMOVE_TIME_SLOT_WITH_WINNERS_MESSAGE,
  formatWinningTimeLabel,
  matchesTimeSlotSearch,
  removeWinnerFromTimeSlot,
  removeWinningTimeSlot,
  updateWinnerInTimeSlot,
  updateWinningTimeSlot,
  updateWinningTimeSlotActive,
  updateWinningTimeSlotPrize,
  WINNING_TIME_WITHOUT_PRIZE_MESSAGE,
  type GeneratedWinningTimeSlotInput,
} from '@/lib/raspadinha-campaigns'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import styles from './RaspadinhaWeeklyWinningSchedules.module.css'
import { AutoGenerateTimeSlotsModal } from './AutoGenerateTimeSlotsModal'
import { WinnerDetailPanel } from './WinnerDetailPanel'
import { WinnerFormModal, type WinnerFormValues } from './WinnerFormModal'

type RaspadinhaDateSchedulesEditorProps = {
  schedules: RaspadinhaDayWinningSchedule[]
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  startsAt: string | null
  endsAt: string | null
  onSchedulesChange: (next: RaspadinhaDayWinningSchedule[]) => void
  onPendingSchedulesChange?: (hasPending: boolean) => void
}

function hasPendingGeneratedTimes(
  pendingByDate: Partial<Record<string, GeneratedWinningTimeSlotInput[]>>
): boolean {
  return Object.values(pendingByDate).some((slots) => (slots?.length ?? 0) > 0)
}

type WinnerModalState = {
  date: string
  slotId: string
  winner: RaspadinhaTimeSlotWinner | null
}

function DisplayCell({ value }: { value: string }) {
  const trimmed = value.trim()
  return (
    <span className={trimmed ? styles.cellText : styles.cellTextMuted}>
      {trimmed || '—'}
    </span>
  )
}

function isInteractiveRowTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(target.closest('button, input, select, [role="switch"]'))
}

function stopRowToggle(event: { stopPropagation: () => void }) {
  event.stopPropagation()
}

type WinnersTableProps = {
  slot: RaspadinhaWinningTimeSlot
  onAddWinner: () => void
  onEditWinner: (winner: RaspadinhaTimeSlotWinner) => void
  onRemoveWinner: (winnerId: string) => void
}

function WinnersTable({ slot, onAddWinner, onEditWinner, onRemoveWinner }: WinnersTableProps) {
  const [expandedWinnerId, setExpandedWinnerId] = useState<string | null>(null)

  useEffect(() => {
    setExpandedWinnerId((previous) =>
      previous && slot.winners.some((winner) => winner.id === previous) ? previous : null
    )
  }, [slot.winners])

  const handleWinnerRowClick = (winnerId: string, event: MouseEvent<HTMLTableRowElement>) => {
    if (isInteractiveRowTarget(event.target)) return

    setExpandedWinnerId((previous) => (previous === winnerId ? null : winnerId))
  }

  return (
    <div className={styles.winnersPanel}>
      <div className={styles.winnersPanelHeader}>
        <p className={styles.winnersPanelTitle}>
          Ganhadores — {formatWinningTimeLabel(slot.time)}
        </p>
        <button
          type="button"
          className={`${styles.toolbarButton} ${styles.toolbarButtonPrimary}`}
          onClick={(event) => {
            stopRowToggle(event)
            onAddWinner()
          }}
        >
          <Icon icon="mdi:plus" aria-hidden />
          Adicionar ganhador
        </button>
      </div>

      {slot.winners.length === 0 ? (
        <p className={styles.emptyWinnersHint}>Nenhum ganhador cadastrado neste horário.</p>
      ) : (
        <div className={styles.winnersTableWrap}>
          <div className={styles.tableWrap}>
            <table className={`${styles.dataTable} ${styles.winnersTable}`}>
              <thead>
                <tr>
                  <th className={styles.colWinnerExpand} aria-hidden />
                  <th className={styles.colName}>Nome</th>
                  <th className={styles.colDocument}>CPF / documento</th>
                  <th className={styles.colPrize}>Prêmio</th>
                  <th className={styles.colNotes}>Observações</th>
                  <th className={styles.colRowActions}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {slot.winners.map((winner) => {
                  const isExpanded = expandedWinnerId === winner.id

                  return (
                    <Fragment key={winner.id}>
                      <tr
                        className={`${styles.winnerRow} ${isExpanded ? styles.winnerRowExpanded : ''}`}
                        onClick={(event) => handleWinnerRowClick(winner.id, event)}
                        aria-expanded={isExpanded}
                      >
                        <td className={styles.colWinnerExpand}>
                          <Icon
                            icon="mdi:chevron-right"
                            className={`${styles.weekdayToggleIcon} ${isExpanded ? styles.weekdayToggleIconExpanded : ''}`}
                            aria-hidden
                          />
                        </td>
                        <td>
                          <DisplayCell value={winner.name} />
                        </td>
                        <td>
                          <DisplayCell value={winner.document} />
                        </td>
                        <td>
                          <DisplayCell value={winner.prize_label} />
                        </td>
                        <td>
                          <DisplayCell value={winner.notes} />
                        </td>
                        <td className={styles.colRowActions}>
                          <button
                            type="button"
                            className={styles.iconActionButton}
                            onClick={(event) => {
                              stopRowToggle(event)
                              onEditWinner(winner)
                            }}
                            aria-label={`Editar ganhador ${winner.name || 'sem nome'}`}
                            title="Editar ganhador"
                          >
                            <Icon icon="mdi:pencil-outline" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconActionButton} ${styles.iconActionButtonDanger}`}
                            onClick={(event) => {
                              stopRowToggle(event)
                              onRemoveWinner(winner.id)
                            }}
                            aria-label={`Remover ganhador ${winner.name || 'sem nome'}`}
                            title="Remover ganhador"
                          >
                            <Icon icon="mdi:delete-outline" aria-hidden />
                          </button>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr className={styles.winnerDetailRow}>
                          <td colSpan={6}>
                            <WinnerDetailPanel winner={winner} slotTime={slot.time} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

type TimeSlotsTableProps = {
  slots: RaspadinhaWinningTimeSlot[]
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  expandedSlotIds: ReadonlySet<string>
  onToggleSlot: (slotId: string) => void
  onTimeChange: (slotId: string, time: string) => void
  onPrizeChange: (slotId: string, contemplatedPrizeId: string) => void
  onActiveChange: (slotId: string, isActive: boolean) => void
  onRemoveSlot: (slotId: string) => void
  onOpenWinnerModal: (slotId: string, winner: RaspadinhaTimeSlotWinner | null) => void
  onRemoveWinner: (slotId: string, winnerId: string) => void
  onScheduleActionError: (message: string | null) => void
}

function TimeSlotsTable({
  slots,
  contemplatedPrizes,
  expandedSlotIds,
  onToggleSlot,
  onTimeChange,
  onPrizeChange,
  onActiveChange,
  onRemoveSlot,
  onOpenWinnerModal,
  onRemoveWinner,
  onScheduleActionError,
}: TimeSlotsTableProps) {
  const [deactivateHintSlotId, setDeactivateHintSlotId] = useState<string | null>(null)

  const handleSlotRowClick = (slotId: string, event: MouseEvent<HTMLTableRowElement>) => {
    if (isInteractiveRowTarget(event.target)) return
    onToggleSlot(slotId)
  }

  const handleRemoveAttempt = (slot: RaspadinhaWinningTimeSlot) => {
    if (!canRemoveWinningTimeSlot(slot)) {
      setDeactivateHintSlotId(slot.id)
      return
    }

    setDeactivateHintSlotId(null)
    onRemoveSlot(slot.id)
  }

  const handleActiveChangeWithHint = (slotId: string, isActive: boolean) => {
    onActiveChange(slotId, isActive)
    if (deactivateHintSlotId === slotId && !isActive) {
      setDeactivateHintSlotId(null)
    }
  }

  return (
    <div className={styles.timeSlotsSection}>
      <p className={styles.timeSlotsSectionLabel}>Horários de contemplação</p>
      <div className={styles.tableWrap}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th className={styles.colExpand} aria-hidden />
              <th className={styles.colTime}>Horário</th>
              <th className={styles.colPrize}>Prêmio</th>
              <th className={styles.colStatus}>Status</th>
              <th className={styles.colCount}>Ganhadores</th>
              <th className={styles.colActions}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {slots.map((slot) => {
              const isExpanded = expandedSlotIds.has(slot.id)
              const hasPrize = hasWinningTimeSlotPrize(slot)
              const canEditTime = hasPrize

              return (
                <Fragment key={slot.id}>
                  <tr
                    className={`${styles.slotRow} ${isExpanded ? styles.slotRowExpanded : ''} ${slot.is_active ? '' : styles.slotRowInactive}`}
                    onClick={(event) => handleSlotRowClick(slot.id, event)}
                    aria-expanded={isExpanded}
                  >
                    <td className={styles.colExpand}>
                      <Icon
                        icon="mdi:chevron-right"
                        className={`${styles.weekdayToggleIcon} ${isExpanded ? styles.weekdayToggleIconExpanded : ''}`}
                        aria-hidden
                      />
                    </td>
                    <td className={styles.colTime}>
                      <input
                        type="time"
                        className={styles.timeInput}
                        value={slot.time}
                        disabled={!canEditTime}
                        onClick={stopRowToggle}
                        onKeyDown={stopRowToggle}
                        onChange={(event) => {
                          const nextTime = event.target.value
                          if (!canSetWinningTimeSlotTime(slot, nextTime)) {
                            onScheduleActionError(WINNING_TIME_WITHOUT_PRIZE_MESSAGE)
                            return
                          }

                          onScheduleActionError(null)
                          onTimeChange(slot.id, nextTime)
                        }}
                        aria-label="Horário da contemplação"
                        title={
                          canEditTime
                            ? undefined
                            : 'Selecione o prêmio antes de definir o horário'
                        }
                        required={hasPrize}
                      />
                    </td>
                    <td className={styles.colPrize}>
                      <select
                        className={`${styles.prizeSelect} ${!hasPrize ? styles.prizeSelectInvalid : ''}`}
                        value={slot.contemplated_prize_id}
                        onClick={stopRowToggle}
                        onKeyDown={stopRowToggle}
                        onChange={(event) => {
                          const nextPrizeId = event.target.value
                          if (nextPrizeId === '' && !canClearWinningTimeSlotPrize(slot)) {
                            onScheduleActionError(WINNING_TIME_WITHOUT_PRIZE_MESSAGE)
                            return
                          }

                          onScheduleActionError(null)
                          onPrizeChange(slot.id, nextPrizeId)
                        }}
                        aria-label={`Prêmio do horário ${formatWinningTimeLabel(slot.time)}`}
                        aria-invalid={!hasPrize}
                        required
                      >
                        <option value="" disabled>
                          Selecione o prêmio
                        </option>
                        {buildContemplatedPrizeSelectOptions(
                          contemplatedPrizes,
                          slot.contemplated_prize_id
                        ).map((prize) => (
                          <option key={prize.id} value={prize.id}>
                            {prize.title.trim() || '(Sem título)'}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={styles.colStatus}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={slot.is_active}
                        className={`${styles.toggleSwitch} ${slot.is_active ? styles.toggleSwitchOn : styles.toggleSwitchOff}`}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          stopRowToggle(event)
                          handleActiveChangeWithHint(slot.id, !slot.is_active)
                        }}
                        aria-label={`${slot.is_active ? 'Desativar' : 'Ativar'} horário ${formatWinningTimeLabel(slot.time)}`}
                      >
                        <span className={styles.toggleTrack} aria-hidden />
                        <span className={styles.toggleLabel}>
                          {slot.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </button>
                    </td>
                    <td className={styles.colCount}>
                      <span className={styles.countBadge}>{slot.winners.length}</span>
                    </td>
                    <td className={styles.colActions}>
                      <button
                        type="button"
                        className={styles.iconActionButton}
                        onClick={(event) => {
                          stopRowToggle(event)
                          onOpenWinnerModal(slot.id, null)
                        }}
                        aria-label="Adicionar ganhador"
                        title="Adicionar ganhador"
                      >
                        <Icon icon="mdi:account-plus-outline" aria-hidden />
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconActionButton} ${styles.iconActionButtonDanger} ${!canRemoveWinningTimeSlot(slot) ? styles.iconActionButtonMuted : ''}`}
                        onClick={(event) => {
                          stopRowToggle(event)
                          handleRemoveAttempt(slot)
                        }}
                        aria-label={
                          canRemoveWinningTimeSlot(slot)
                            ? 'Remover horário'
                            : 'Não é possível remover horário com ganhadores'
                        }
                        title={
                          canRemoveWinningTimeSlot(slot)
                            ? 'Remover horário'
                            : REMOVE_TIME_SLOT_WITH_WINNERS_MESSAGE
                        }
                      >
                        <Icon icon="mdi:delete-outline" aria-hidden />
                      </button>
                    </td>
                  </tr>
                  {deactivateHintSlotId === slot.id ? (
                    <tr className={styles.deleteBlockedRow}>
                      <td colSpan={6}>
                        <div className={styles.deleteBlockedHint} role="status">
                          <Icon icon="mdi:information-outline" aria-hidden />
                          <p>{REMOVE_TIME_SLOT_WITH_WINNERS_MESSAGE}</p>
                          <button
                            type="button"
                            className={styles.deleteBlockedAction}
                            onClick={(event) => {
                              stopRowToggle(event)
                              handleActiveChangeWithHint(slot.id, false)
                            }}
                          >
                            Desativar horário
                          </button>
                          <button
                            type="button"
                            className={styles.deleteBlockedDismiss}
                            onClick={(event) => {
                              stopRowToggle(event)
                              setDeactivateHintSlotId(null)
                            }}
                            aria-label="Fechar aviso"
                          >
                            <Icon icon="mdi:close" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  {isExpanded ? (
                    <tr className={styles.expandedRow}>
                      <td colSpan={6}>
                        <WinnersTable
                          slot={slot}
                          onAddWinner={() => onOpenWinnerModal(slot.id, null)}
                          onEditWinner={(winner) => onOpenWinnerModal(slot.id, winner)}
                          onRemoveWinner={(winnerId) => onRemoveWinner(slot.id, winnerId)}
                        />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type PendingGeneratedTimeSlotsPanelProps = {
  slots: readonly GeneratedWinningTimeSlotInput[]
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  onDiscard: () => void
}

function PendingGeneratedTimeSlotsPanel({
  slots,
  contemplatedPrizes,
  onDiscard,
}: PendingGeneratedTimeSlotsPanelProps) {
  return (
    <div className={styles.pendingGeneratedPanel} role="status">
      <div className={styles.pendingGeneratedHeader}>
        <div>
          <p className={styles.pendingGeneratedTitle}>
            <Icon icon="mdi:clock-check-outline" aria-hidden />
            Horários gerados — revise antes de salvar
          </p>
          <p className={styles.pendingGeneratedHint}>
            {slots.length} horário(s) aguardando confirmação. Clique em{' '}
            <strong>Salvar Horários</strong> para adicioná-los ao dia.
          </p>
        </div>
        <button type="button" className={styles.pendingGeneratedDiscard} onClick={onDiscard}>
          Descartar
        </button>
      </div>
      <ul className={styles.pendingGeneratedList}>
        {slots.map((slot) => {
          const prizeTitle = getContemplatedPrizeTitle(contemplatedPrizes, slot.contemplated_prize_id)

          return (
            <li
              key={`${slot.time}-${slot.contemplated_prize_id}`}
              className={styles.pendingGeneratedChip}
            >
              <span className={styles.pendingGeneratedChipTime}>{slot.time}</span>
              <span className={styles.pendingGeneratedChipPrize}>
                {prizeTitle || 'Prêmio não definido'}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

type DateCollapsibleProps = {
  date: string
  label: string
  dateStatus: ReturnType<typeof getCampaignDateStatus>
  schedule: RaspadinhaDayWinningSchedule
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  pendingGeneratedSlots: readonly GeneratedWinningTimeSlotInput[]
  isExpanded: boolean
  searchQuery: string
  expandedSlotIds: ReadonlySet<string>
  onToggleDay: (date: string) => void
  onSearchChange: (date: string, value: string) => void
  onAddTimeSlot: (date: string) => void
  onOpenAutoGenerateModal: (date: string) => void
  onSaveGeneratedTimeSlots: (date: string) => void
  onDiscardPendingGenerated: (date: string) => void
  onToggleSlot: (slotId: string) => void
  onTimeChange: (slotId: string, time: string) => void
  onPrizeChange: (slotId: string, contemplatedPrizeId: string) => void
  onActiveChange: (slotId: string, isActive: boolean) => void
  onRemoveSlot: (slotId: string) => void
  onOpenWinnerModal: (slotId: string, winner: RaspadinhaTimeSlotWinner | null) => void
  onRemoveWinner: (slotId: string, winnerId: string) => void
  canAddTimeSlots: boolean
  scheduleActionError: string | null
  onScheduleActionError: (message: string | null) => void
}

function DateCollapsible({
  date,
  label,
  dateStatus,
  schedule,
  contemplatedPrizes,
  pendingGeneratedSlots,
  isExpanded,
  searchQuery,
  expandedSlotIds,
  onToggleDay,
  onSearchChange,
  onAddTimeSlot,
  onOpenAutoGenerateModal,
  onSaveGeneratedTimeSlots,
  onDiscardPendingGenerated,
  onToggleSlot,
  onTimeChange,
  onPrizeChange,
  onActiveChange,
  onRemoveSlot,
  onOpenWinnerModal,
  onRemoveWinner,
  canAddTimeSlots,
  scheduleActionError,
  onScheduleActionError,
}: DateCollapsibleProps) {
  const filteredSlots = useMemo(() => {
    if (!searchQuery.trim()) return schedule.time_slots
    return schedule.time_slots.filter((slot) =>
      matchesTimeSlotSearch(slot, searchQuery, contemplatedPrizes)
    )
  }, [contemplatedPrizes, schedule.time_slots, searchQuery])

  const winnersCount = countWinnersInDaySchedule(schedule)
  const hasPendingGenerated = pendingGeneratedSlots.length > 0

  const cardStatusClass =
    dateStatus === 'past'
      ? styles.weekdayCardPast
      : dateStatus === 'today'
        ? styles.weekdayCardToday
        : ''

  return (
    <article className={`${styles.weekdayCard} ${cardStatusClass}`.trim()}>
      <button
        type="button"
        className={`${styles.weekdayToggle} ${isExpanded ? styles.weekdayToggleExpanded : ''}`}
        onClick={() => onToggleDay(date)}
        aria-expanded={isExpanded}
      >
        <span className={styles.weekdayToggleMain}>
          <Icon
            icon="mdi:chevron-right"
            className={`${styles.weekdayToggleIcon} ${isExpanded ? styles.weekdayToggleIconExpanded : ''}`}
            aria-hidden
          />
          <h4 className={styles.weekdayTitle}>{label}</h4>
          <span className={styles.weekdayMeta}>
            <span className={styles.weekdayBadge}>
              {schedule.time_slots.length} horário(s)
            </span>
            <span className={`${styles.weekdayBadge} ${styles.weekdayBadgeAccent}`}>
              {winnersCount} ganhador(es)
            </span>
            {hasPendingGenerated ? (
              <span className={`${styles.weekdayBadge} ${styles.weekdayBadgePending}`}>
                {pendingGeneratedSlots.length} aguardando salvamento
              </span>
            ) : null}
          </span>
        </span>
        <span className={styles.weekdayExpandHint}>
          {isExpanded ? 'Recolher dia' : 'Expandir dia'}
        </span>
      </button>

      {isExpanded ? (
        <div className={styles.weekdayBody}>
          <div className={styles.weekdayToolbar}>
            <input
              type="search"
              className={`${editStyles.editInput} ${styles.searchInput}`}
              value={searchQuery}
              onChange={(event) => onSearchChange(date, event.target.value)}
              placeholder="Buscar horário, nome, CPF ou prêmio..."
              aria-label={`Buscar horários de ${label}`}
            />
            <div className={styles.toolbarActions}>
              <button
                type="button"
                className={`${styles.toolbarButton} ${styles.toolbarButtonPrimary}`}
                onClick={() => onAddTimeSlot(date)}
                disabled={!canAddTimeSlots}
                title={
                  canAddTimeSlots
                    ? 'Adicionar horário de contemplação'
                    : ADD_TIME_SLOT_REQUIRES_PRIZE_CATALOG_MESSAGE
                }
              >
                <Icon icon="mdi:plus" aria-hidden />
                Adicionar horário
              </button>
              <button
                type="button"
                className={styles.toolbarButton}
                onClick={() => onOpenAutoGenerateModal(date)}
              >
                <Icon icon="mdi:auto-fix" aria-hidden />
                Gerar horários
              </button>
              <button
                type="button"
                className={`${styles.toolbarButton} ${styles.toolbarButtonSave}`}
                onClick={() => onSaveGeneratedTimeSlots(date)}
                disabled={!hasPendingGenerated}
                title={
                  hasPendingGenerated
                    ? 'Confirmar e adicionar os horários gerados neste dia'
                    : 'Gere horários antes de salvar'
                }
              >
                <Icon icon="mdi:content-save-outline" aria-hidden />
                Salvar Horários
              </button>
            </div>
          </div>

          {!canAddTimeSlots ? (
            <p className={styles.addTimeSlotHint}>{ADD_TIME_SLOT_REQUIRES_PRIZE_CATALOG_MESSAGE}</p>
          ) : null}

          {scheduleActionError ? (
            <p className={styles.scheduleActionError} role="alert">
              {scheduleActionError}
            </p>
          ) : null}

          {hasPendingGenerated ? (
            <PendingGeneratedTimeSlotsPanel
              slots={pendingGeneratedSlots}
              contemplatedPrizes={contemplatedPrizes}
              onDiscard={() => onDiscardPendingGenerated(date)}
            />
          ) : null}

          {schedule.time_slots.length === 0 && !hasPendingGenerated ? (
            <p className={styles.emptyDayHint}>Nenhum horário neste dia.</p>
          ) : schedule.time_slots.length > 0 && filteredSlots.length === 0 ? (
            <p className={styles.emptyFilterHint}>Nenhum resultado para a busca.</p>
          ) : (
            <TimeSlotsTable
              slots={filteredSlots}
              contemplatedPrizes={contemplatedPrizes}
              expandedSlotIds={expandedSlotIds}
              onToggleSlot={onToggleSlot}
              onTimeChange={(slotId, time) => onTimeChange(slotId, time)}
              onPrizeChange={onPrizeChange}
              onActiveChange={onActiveChange}
              onRemoveSlot={onRemoveSlot}
              onOpenWinnerModal={(slotId, winner) => onOpenWinnerModal(slotId, winner)}
              onRemoveWinner={(slotId, winnerId) => onRemoveWinner(slotId, winnerId)}
              onScheduleActionError={onScheduleActionError}
            />
          )}
        </div>
      ) : null}
    </article>
  )
}

export function RaspadinhaDateSchedulesEditor({
  schedules,
  contemplatedPrizes,
  startsAt,
  endsAt,
  onSchedulesChange,
  onPendingSchedulesChange,
}: RaspadinhaDateSchedulesEditorProps) {
  const [expandedDates, setExpandedDates] = useState<ReadonlySet<string>>(() => new Set())
  const [expandedSlotIds, setExpandedSlotIds] = useState<ReadonlySet<string>>(() => new Set())
  const [searchByDate, setSearchByDate] = useState<Partial<Record<string, string>>>({})
  const [winnerModal, setWinnerModal] = useState<WinnerModalState | null>(null)
  const [autoGenerateDate, setAutoGenerateDate] = useState<string | null>(null)
  const [autoGenerateDayNumber, setAutoGenerateDayNumber] = useState<number | null>(null)
  const [pendingGeneratedByDate, setPendingGeneratedByDate] = useState<
    Partial<Record<string, GeneratedWinningTimeSlotInput[]>>
  >({})
  const [scheduleActionErrorByDate, setScheduleActionErrorByDate] = useState<
    Partial<Record<string, string>>
  >({})

  const setScheduleActionErrorForDate = useCallback((date: string, message: string | null) => {
    setScheduleActionErrorByDate((previous) => {
      if (message === null) {
        if (!previous[date]) {
          return previous
        }
        const next = { ...previous }
        delete next[date]
        return next
      }

      return { ...previous, [date]: message }
    })
  }, [])

  const resolvedSchedules = useMemo(
    () => ensureDateWinningSchedules(schedules, startsAt, endsAt),
    [schedules, startsAt, endsAt]
  )

  const canAddTimeSlots = contemplatedPrizes.length > 0

  useEffect(() => {
    onPendingSchedulesChange?.(hasPendingGeneratedTimes(pendingGeneratedByDate))
  }, [onPendingSchedulesChange, pendingGeneratedByDate])

  const toggleDate = useCallback((date: string) => {
    setExpandedDates((previous) => {
      const next = new Set(previous)
      if (next.has(date)) {
        next.delete(date)
      } else {
        next.add(date)
      }
      return next
    })
  }, [])

  const toggleSlot = useCallback((slotId: string) => {
    setExpandedSlotIds((previous) => {
      const next = new Set(previous)
      if (next.has(slotId)) {
        next.delete(slotId)
      } else {
        next.add(slotId)
      }
      return next
    })
  }, [])

  const handleAddTimeSlot = useCallback(
    (date: string) => {
      if (contemplatedPrizes.length === 0) {
        setScheduleActionErrorForDate(date, ADD_TIME_SLOT_REQUIRES_PRIZE_CATALOG_MESSAGE)
        return
      }

      const defaultPrizeId =
        contemplatedPrizes.length === 1 ? contemplatedPrizes[0]?.id ?? '' : ''

      const nextSchedules = addWinningTimeSlot(resolvedSchedules, date, defaultPrizeId)
      const daySchedule = nextSchedules.find((schedule) => schedule.date === date)
      const newSlotId = daySchedule?.time_slots[daySchedule.time_slots.length - 1]?.id

      setScheduleActionErrorForDate(date, null)
      onSchedulesChange(nextSchedules)
      setExpandedDates((previous) => new Set(previous).add(date))

      if (newSlotId) {
        setExpandedSlotIds((previous) => new Set(previous).add(newSlotId))
      }
    },
    [contemplatedPrizes, onSchedulesChange, resolvedSchedules, setScheduleActionErrorForDate]
  )

  const handleRemoveSlot = useCallback(
    (date: string, slotId: string) => {
      try {
        onSchedulesChange(removeWinningTimeSlot(resolvedSchedules, date, slotId))
        setExpandedSlotIds((previous) => {
          const next = new Set(previous)
          next.delete(slotId)
          return next
        })
        setWinnerModal((previous) =>
          previous?.slotId === slotId && previous.date === date ? null : previous
        )
      } catch {
        // Bloqueio tratado na UI antes da exclusão.
      }
    },
    [onSchedulesChange, resolvedSchedules]
  )

  const handleTimeChange = useCallback(
    (date: string, slotId: string, time: string) => {
      onSchedulesChange(updateWinningTimeSlot(resolvedSchedules, date, slotId, time))
    },
    [onSchedulesChange, resolvedSchedules]
  )

  const handleActiveChange = useCallback(
    (date: string, slotId: string, isActive: boolean) => {
      onSchedulesChange(updateWinningTimeSlotActive(resolvedSchedules, date, slotId, isActive))
    },
    [onSchedulesChange, resolvedSchedules]
  )

  const handlePrizeChange = useCallback(
    (date: string, slotId: string, contemplatedPrizeId: string) => {
      onSchedulesChange(
        updateWinningTimeSlotPrize(resolvedSchedules, date, slotId, contemplatedPrizeId)
      )
    },
    [onSchedulesChange, resolvedSchedules]
  )

  const handleOpenWinnerModal = useCallback(
    (date: string, slotId: string, winner: RaspadinhaTimeSlotWinner | null) => {
      setWinnerModal({ date, slotId, winner })
      setExpandedSlotIds((previous) => new Set(previous).add(slotId))
      setExpandedDates((previous) => new Set(previous).add(date))
    },
    []
  )

  const handleSaveWinner = useCallback(
    (values: WinnerFormValues) => {
      if (!winnerModal) return

      const { date, slotId, winner } = winnerModal

      if (winner) {
        onSchedulesChange(
          updateWinnerInTimeSlot(resolvedSchedules, date, slotId, winner.id, values)
        )
      } else {
        const withNewWinner = addWinnerToTimeSlot(resolvedSchedules, date, slotId)
        const daySchedule = withNewWinner.find((schedule) => schedule.date === date)
        const slot = daySchedule?.time_slots.find((entry) => entry.id === slotId)
        const createdWinner = slot?.winners[slot.winners.length - 1]
        const slotPrizeLabel = slot
          ? getContemplatedPrizeTitle(contemplatedPrizes, slot.contemplated_prize_id)
          : ''

        if (!createdWinner) return

        onSchedulesChange(
          updateWinnerInTimeSlot(withNewWinner, date, slotId, createdWinner.id, {
            ...values,
            prize_label: values.prize_label.trim() || slotPrizeLabel,
            receipt_signature: createMockPrizeReceiptSignature(createdWinner.id),
          })
        )
      }

      setWinnerModal(null)
    },
    [contemplatedPrizes, onSchedulesChange, resolvedSchedules, winnerModal]
  )

  const handleRemoveWinner = useCallback(
    (date: string, slotId: string, winnerId: string) => {
      onSchedulesChange(removeWinnerFromTimeSlot(resolvedSchedules, date, slotId, winnerId))
      setWinnerModal((previous) =>
        previous?.winner?.id === winnerId ? null : previous
      )
    },
    [onSchedulesChange, resolvedSchedules]
  )

  const handleSearchChange = useCallback((date: string, value: string) => {
    setSearchByDate((previous) => ({ ...previous, [date]: value }))
  }, [])

  const occupiedTimesForAutoGenerate = useMemo(() => {
    if (!autoGenerateDate) return []

    const daySchedule = resolvedSchedules.find((schedule) => schedule.date === autoGenerateDate)
    const savedTimes =
      daySchedule?.time_slots
        .map((slot) => slot.time)
        .filter((time) => isValidWinningTime(time)) ?? []
    const pendingTimes =
      pendingGeneratedByDate[autoGenerateDate]?.map((slot) => slot.time) ?? []

    return [...savedTimes, ...pendingTimes]
  }, [autoGenerateDate, pendingGeneratedByDate, resolvedSchedules])

  const handlePreviewGeneratedTimeSlots = useCallback(
    (date: string, generatedSlots: GeneratedWinningTimeSlotInput[]) => {
      setPendingGeneratedByDate((previous) => ({
        ...previous,
        [date]: generatedSlots,
      }))
      setExpandedDates((previous) => new Set(previous).add(date))
    },
    []
  )

  const handleSaveGeneratedTimeSlots = useCallback(
    (date: string) => {
      const pendingSlots = pendingGeneratedByDate[date]
      if (!pendingSlots?.length) return

      onSchedulesChange(addGeneratedWinningTimeSlots(resolvedSchedules, date, pendingSlots))
      setPendingGeneratedByDate((previous) => {
        const next = { ...previous }
        delete next[date]
        return next
      })
      setExpandedDates((previous) => new Set(previous).add(date))
    },
    [onSchedulesChange, pendingGeneratedByDate, resolvedSchedules]
  )

  const handleDiscardPendingGenerated = useCallback((date: string) => {
    setPendingGeneratedByDate((previous) => {
      const next = { ...previous }
      delete next[date]
      return next
    })
  }, [])

  if (resolvedSchedules.length === 0) {
    return (
      <p className={editStyles.editLabelHint}>
        Defina a data de início e de encerramento da campanha para configurar as contemplações.
      </p>
    )
  }

  return (
    <>
      <div className={styles.weekdayList}>
        {resolvedSchedules.map((daySchedule, index) => {
          const date = daySchedule.date
          const label = formatCampaignDateLabel(date, index + 1)
          const dateStatus = getCampaignDateStatus(date)

          return (
            <DateCollapsible
              key={date}
              date={date}
              label={label}
              dateStatus={dateStatus}
              schedule={daySchedule}
              contemplatedPrizes={contemplatedPrizes}
              pendingGeneratedSlots={pendingGeneratedByDate[date] ?? []}
              isExpanded={expandedDates.has(date)}
              searchQuery={searchByDate[date] ?? ''}
              expandedSlotIds={expandedSlotIds}
              onToggleDay={toggleDate}
              onSearchChange={handleSearchChange}
              onAddTimeSlot={handleAddTimeSlot}
              onOpenAutoGenerateModal={(selectedDate) => {
                setAutoGenerateDate(selectedDate)
                setAutoGenerateDayNumber(index + 1)
              }}
              onSaveGeneratedTimeSlots={handleSaveGeneratedTimeSlots}
              onDiscardPendingGenerated={handleDiscardPendingGenerated}
              onToggleSlot={toggleSlot}
              onTimeChange={(slotId, time) => handleTimeChange(date, slotId, time)}
              onPrizeChange={(slotId, contemplatedPrizeId) =>
                handlePrizeChange(date, slotId, contemplatedPrizeId)
              }
              onActiveChange={(slotId, isActive) => handleActiveChange(date, slotId, isActive)}
              onRemoveSlot={(slotId) => handleRemoveSlot(date, slotId)}
              onOpenWinnerModal={(slotId, winner) => handleOpenWinnerModal(date, slotId, winner)}
              onRemoveWinner={(slotId, winnerId) => handleRemoveWinner(date, slotId, winnerId)}
              canAddTimeSlots={canAddTimeSlots}
              scheduleActionError={scheduleActionErrorByDate[date] ?? null}
              onScheduleActionError={(message) => setScheduleActionErrorForDate(date, message)}
            />
          )
        })}
      </div>

      <WinnerFormModal
        isOpen={winnerModal !== null}
        mode={winnerModal?.winner ? 'edit' : 'create'}
        initialWinner={winnerModal?.winner ?? null}
        onClose={() => setWinnerModal(null)}
        onSave={handleSaveWinner}
      />

      <AutoGenerateTimeSlotsModal
        isOpen={autoGenerateDate !== null}
        date={autoGenerateDate}
        dayNumber={autoGenerateDayNumber}
        contemplatedPrizes={contemplatedPrizes}
        occupiedTimes={occupiedTimesForAutoGenerate}
        onClose={() => {
          setAutoGenerateDate(null)
          setAutoGenerateDayNumber(null)
        }}
        onGenerate={(generatedSlots) => {
          if (!autoGenerateDate) return
          handlePreviewGeneratedTimeSlots(autoGenerateDate, generatedSlots)
        }}
      />
    </>
  )
}
